import { useEffect, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { LoadingGrid, StatCard } from "@/components/bits";
import api, { inr } from "@/lib/api";

const COLORS = ["#2563EB", "#F5A623", "#0B1B3D", "#10B981", "#8B5CF6", "#EC4899"];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => setError(true));
  }, []);

  if (error)
    return <div className="border border-dashed border-border rounded-xl py-16 text-center" data-testid="admin-analytics-error">
      <p className="font-heading font-semibold text-lg text-[#0B1B3D]">Analytics unavailable</p>
    </div>;
  if (!data) return <LoadingGrid count={4} />;

  const totalRevenue = data.revenue_by_kind.reduce((s, k) => s + k.revenue, 0);

  return (
    <div data-testid="admin-analytics">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard label="Total Revenue" value={inr(totalRevenue)} accent testId="stat-revenue" />
        <StatCard label="Revenue Streams" value={data.revenue_by_kind.length} />
        <StatCard label="Active Plans Mix" value={data.plan_mix.reduce((s, p) => s + p.count, 0)} />
        <StatCard label="Churned / Replaced Subs" value={data.subscriptions_expired_churned} testId="stat-churn" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="revenue-trend-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Revenue trend</h3>
          {data.revenue_trend.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">No paid invoices yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.revenue_trend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="revenue" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="revenue-kind-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Revenue by stream</h3>
          {data.revenue_by_kind.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">No revenue yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.revenue_by_kind} dataKey="revenue" nameKey="kind" innerRadius={50} outerRadius={85} label>
                  {data.revenue_by_kind.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="plan-mix-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Subscription plan mix</h3>
          {data.plan_mix.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">No active subscriptions.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.plan_mix}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="plan" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#F5A623" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="apps-trend-chart">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Applications & placements</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.applications_trend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="applications" fill="#2563EB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="joined" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="partner-leaderboard">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Partner leaderboard (payouts)</h3>
          {data.partner_leaderboard.length === 0 ? (
            <p className="text-sm text-muted-foreground">No partner payouts yet.</p>
          ) : (
            <div className="space-y-2">
              {data.partner_leaderboard.map((p, i) => (
                <div key={p.name} className="flex items-center justify-between border border-border/60 rounded-lg px-4 py-2.5">
                  <p className="text-sm font-medium">#{i + 1} {p.name}</p>
                  <p className="font-heading font-bold text-sm">{inr(p.earnings)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="institute-leaderboard">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Institute leaderboard (course revenue)</h3>
          {data.institute_leaderboard.length === 0 ? (
            <p className="text-sm text-muted-foreground">No institute payouts yet.</p>
          ) : (
            <div className="space-y-2">
              {data.institute_leaderboard.map((p, i) => (
                <div key={p.name} className="flex items-center justify-between border border-border/60 rounded-lg px-4 py-2.5">
                  <p className="text-sm font-medium">#{i + 1} {p.name}</p>
                  <p className="font-heading font-bold text-sm">{inr(p.earnings)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
